import { formatPrice } from '@ynap/intl-formatters';
import { priceFromPriceObj, subtractPriceObjs } from '@ynap/price-utils';

export default function getPrice(details, locale, taxType, lineLevelTaxesEnabled) {
    const {
        xitem_wasOrderItemPrice,
        catalogEntryView,
        salesTax,
        xitem_adjustedOrderItemPrice,
        adjustment = [],
        taxByTaxCategory = [],
        xitem_lineTotal,
        orderItemPrice,
        unitPrice,
        xitem_baseOrderItemPrice,
        xitem_baseOrderItemPrice_Annualised_GBP,
        xitem_percentDiscount,
        shippingTax,
        currency,
    } = details;
    const [{ price: productColourPrice, sKUs }] = catalogEntryView.productColours;

    const catalogCurrencyLabel = sKUs?.[0]?.price?.currency?.label || productColourPrice?.currency?.label || catalogEntryView?.price?.currency?.label;
    const currencyLabel = currency?.label || catalogCurrencyLabel;
    const format = (price) => (price >= 0 && currencyLabel ? formatPrice(price, currencyLabel, locale) : undefined);
    const rawWasOrderItemPrice = priceFromPriceObj(xitem_wasOrderItemPrice);
    const rawLineTotal = priceFromPriceObj(xitem_lineTotal);
    const rawOrderItemPrice = priceFromPriceObj(orderItemPrice);
    const rawSalesTax = priceFromPriceObj(salesTax) || 0;
    const rawAdjustedOrderItemPrice = priceFromPriceObj(xitem_adjustedOrderItemPrice);
    const rawUnitPrice = priceFromPriceObj(unitPrice);
    const promotions = adjustment.reduce((accum, adjustment) => {
        if (adjustment.usage === 'Discount') {
            return [
                ...accum,
                {
                    amount: format(priceFromPriceObj(adjustment.amount)),
                    description: adjustment.description || undefined,
                },
            ];
        }
        return accum;
    }, []);

    const vat = taxByTaxCategory.find((taxType) => taxType.taxCategoryCode === 'ITEM_VAT');
    const duties = taxByTaxCategory.find((taxType) => taxType.taxCategoryCode === 'ITEM_DUTIES');

    const taxesAndDuties =
        vat || duties
            ? {
                  taxAmount: format(priceFromPriceObj(vat?.value)),
                  dutiesAmount: format(priceFromPriceObj(duties?.value)),
                  hasTax: !!vat?.value.amount,
                  hasDuties: !!duties?.value.amount,
              }
            : undefined;

    const DDPIncludedCountry = taxType === 'DDP_INCLUDED';
    const DDPCountry = taxType === 'DDP';

    const markedDownPrice = rawWasOrderItemPrice > rawOrderItemPrice ? format(rawOrderItemPrice) : undefined;
    const adjustedOrderItemPrice = (!!promotions.length && format(rawAdjustedOrderItemPrice)) || markedDownPrice;
    const priceReduced = rawWasOrderItemPrice > rawAdjustedOrderItemPrice;
    const finalPrice =
        (priceReduced || DDPCountry || DDPIncludedCountry || rawSalesTax) && rawOrderItemPrice !== rawLineTotal ? format(rawLineTotal) : undefined;
    const itemSalesTax = taxType === 'US_LOCAL_SALES_TAXES' ? format(rawSalesTax) : undefined;
    const originalPrice = format(Math.max(rawOrderItemPrice, rawWasOrderItemPrice));
    const markedDownPercentDiscount = priceFromPriceObj(sKUs?.find((sku) => sku.partNumber === details.partNumber)?.price?.discount);

    const taxAndDuty = (getTaxAndDuty(taxType, taxByTaxCategory) || 0) + rawSalesTax;
    const priceExTaxDutyWithCurrency = format(rawAdjustedOrderItemPrice);

    const discountAmount = priceFromPriceObj(subtractPriceObjs(xitem_wasOrderItemPrice, xitem_adjustedOrderItemPrice)) || 0;

    const rawShippingTax = priceFromPriceObj(shippingTax);
    const isMarkdownPrice = rawOrderItemPrice && rawOrderItemPrice !== originalPrice;

    const minPrice = priceFromPriceObj(productColourPrice?.minPrice);

    return {
        adjustedOrderItemPrice,
        markedDownPrice,
        priceReduced,
        originalPrice,
        finalPrice,
        taxesAndDuties,
        promotions,
        salesTax: itemSalesTax,
        currency: currencyLabel,
        lineLevelTaxesEnabled,
        DDPIncludedCountry,
        DDPCountry,
        markedDownPercentDiscount,
        rawFinalPrice: lineLevelTaxesEnabled ? rawLineTotal : rawAdjustedOrderItemPrice,
        rawUnitPrice,
        xitem_baseOrderItemPrice,
        xitem_baseOrderItemPrice_Annualised_GBP,
        fullPrice: rawWasOrderItemPrice,
        taxAndDuty,
        displayPriceExTaxDuty: rawAdjustedOrderItemPrice,
        priceExTaxDutyWithCurrency,
        discountAmount,
        rawShippingTax,
        isMarkdownPrice,
        percentDiscount: xitem_percentDiscount,
        minPrice,
    };
}

export function getTaxAndDuty(taxType, taxes = []) {
    if (taxType === 'DDP_INCLUDED') return 0;
    return [...taxes]
        .filter((each) => each.taxCategoryCode === 'ITEM_VAT' || each.taxCategoryCode === 'ITEM_DUTIES')
        .reduce((acc, cur) => {
            return priceFromPriceObj(cur.value) + acc;
        }, 0);
}
